import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Check, Loader2, ShieldCheck, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { fetchCompanies } from '@/api/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { defaultLocale } from '@/lib/locales'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { submitContactCallback } from '@/api/contact'

const contactSchema = z.object({
  firstName: z.string().min(1, 'Prénom requis'),
  lastName: z.string().min(1, 'Nom requis'),
  email: z.string().min(1, 'Courriel requis').email('Courriel invalide'),
  company: z.string().min(1, 'Entreprise requise'),
  phone: z.string().min(7, 'Téléphone invalide'),
  teamSize: z.string().optional(),
  moment: z.string().optional(),
  notes: z.string().optional(),
})

type ContactForm = z.infer<typeof contactSchema>

const TEAM_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+']
const MOMENTS = ['Matin (9h-12h)', 'Après-midi (12h-17h)', 'Soir (17h-20h)']

export function ContactPage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  useDocumentTitle(t('contact.pageTitle', 'Contacter notre équipe'))

  const topic = searchParams.get('topic') ?? 'general'
  const isEnterprise = topic === 'enterprise'

  // Pré-remplit le form depuis le user authentifié + sa company.
  const companiesQuery = useQuery({
    queryKey: ['companies-contact'],
    queryFn: fetchCompanies,
    retry: false,
  })
  const company = companiesQuery.data?.companies?.[0]

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      company: user?.companyName ?? '',
      phone: '',
      teamSize: '',
      moment: '',
      notes: '',
    },
  })

  // Re-sync les valeurs quand la company query arrive après le mount.
  useEffect(() => {
    if (company?.name && !watch('company')) {
      setValue('company', company.name)
    }
  }, [company, setValue, watch])

  const mutation = useMutation({
    mutationFn: submitContactCallback,
    onSuccess: () => {
      toast.success(
        t(
          'contact.success',
          'Demande envoyée. Notre équipe vous contactera sous 1 jour ouvrable.',
        ),
      )
      navigate(`/${locale}/subscribe`, { replace: true })
    },
    onError: () => {
      toast.error(
        t('contact.error', 'Erreur lors de l’envoi. Réessayez ou écrivez à support@callizy.ca.'),
      )
    },
  })

  const onSubmit = (values: ContactForm) => {
    mutation.mutate({
      ...values,
      source: isEnterprise ? 'app_subscribe_enterprise' : 'app_subscribe',
    })
  }

  return (
    <div className="h-full overflow-y-auto w-full grid grid-cols-1 md:grid-cols-5 bg-white text-slate-900">
      {/* LEFT — navy pitch Enterprise */}
      <aside className="hidden md:flex md:col-span-2 flex-col justify-between bg-[#0A192F] text-white p-10">
        <div className="flex items-center gap-3">
          <img
            src="https://sdk.callizy.ca/assets/favicon_callizy.png"
            alt="Callizy"
            className="h-10 w-10"
          />
          <span className="font-bold text-xl">Callizy</span>
        </div>

        <div className="space-y-8">
          {isEnterprise ? (
            <>
              <div>
                <p className="text-xs uppercase tracking-wider text-blue-300/80 font-medium mb-3">
                  Plan Enterprise
                </p>
                <h1 className="text-3xl font-bold leading-tight">
                  Discutons de votre déploiement
                </h1>
                <p className="mt-3 text-white/65 leading-relaxed">
                  Pour les organisations à fort volume, intégrations sur-mesure et exigences contractuelles.
                </p>
              </div>

              <ul className="space-y-3">
                {[
                  'Instance dédiée + hébergement Canada',
                  'White-label avec sous-domaine personnalisé',
                  'SLA 99,99% avec pénalités contractuelles',
                  'Customer Success Manager dédié',
                  'SSO (SAML, Okta, Microsoft Entra)',
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-[14px] text-white/85">
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/20 text-blue-300 shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-[13px] text-white/65 leading-relaxed">
                  Nous reviendrons vers vous dans <span className="text-white font-medium">1 jour ouvrable</span> avec
                  une proposition personnalisée.
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-xs uppercase tracking-wider text-blue-300/80 font-medium mb-3">
                  Contact commercial
                </p>
                <h1 className="text-3xl font-bold leading-tight">Parlez avec notre équipe</h1>
                <p className="mt-3 text-white/65 leading-relaxed">
                  Une question sur votre abonnement, votre déploiement ou une fonctionnalité spécifique ?
                </p>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-blue-300 shrink-0 mt-0.5" />
                <p className="text-[13px] text-white/65 leading-relaxed">
                  Réponse sous <span className="text-white font-medium">1 jour ouvrable</span>.
                </p>
              </div>
            </>
          )}
        </div>

        <Link
          to={`/${locale}/subscribe`}
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors w-fit"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Retour aux plans</span>
        </Link>
      </aside>

      {/* RIGHT — form */}
      <main className="md:col-span-3 p-6 md:p-10 flex items-center justify-center">
        <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-xl space-y-5">
          <div className="md:hidden mb-2">
            <h1 className="text-2xl font-bold tracking-tight">
              {isEnterprise ? 'Discutons de votre déploiement' : 'Parlez avec notre équipe'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Réponse sous 1 jour ouvrable.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">Prénom*</Label>
              <Input id="firstName" {...register('firstName')} placeholder="Martin" />
              {errors.firstName && (
                <p className="text-xs text-red-600">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Nom*</Label>
              <Input id="lastName" {...register('lastName')} placeholder="Tremblay" />
              {errors.lastName && (
                <p className="text-xs text-red-600">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Courriel professionnel*</Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                placeholder="martin@entreprise.ca"
              />
              {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company">Entreprise*</Label>
              <Input
                id="company"
                {...register('company')}
                placeholder="Nom de votre entreprise"
              />
              {errors.company && (
                <p className="text-xs text-red-600">{errors.company.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone">Téléphone*</Label>
              <Input id="phone" type="tel" {...register('phone')} placeholder="+1 (438) 555-0123" />
              {errors.phone && <p className="text-xs text-red-600">{errors.phone.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="teamSize">Taille de l&apos;équipe</Label>
              <Select
                value={watch('teamSize') || ''}
                onValueChange={(v) => setValue('teamSize', v)}
              >
                <SelectTrigger id="teamSize">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {TEAM_SIZES.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size} {size === '1-10' ? 'personnes' : 'personnes'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="moment">Meilleur moment pour vous rappeler</Label>
            <Select value={watch('moment') || ''} onValueChange={(v) => setValue('moment', v)}>
              <SelectTrigger id="moment">
                <SelectValue placeholder="Choisir un créneau" />
              </SelectTrigger>
              <SelectContent>
                {MOMENTS.map((moment) => (
                  <SelectItem key={moment} value={moment}>
                    {moment}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">
              Décrivez votre besoin {isEnterprise ? '*' : '(optionnel)'}
            </Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder={
                isEnterprise
                  ? 'Volume mensuel d’appels, intégrations souhaitées, exigences de conformité…'
                  : 'Votre question ou besoin spécifique…'
              }
              rows={4}
            />
          </div>

          <Button
            type="submit"
            disabled={mutation.isPending}
            className="w-full bg-[#0A192F] text-white hover:bg-[#0A192F]/90 h-11"
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>Envoyer la demande</>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            En soumettant ce formulaire, vous acceptez notre{' '}
            <a
              href="https://callizy.ca/fr/legal/privacy"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-slate-700"
            >
              politique de confidentialité
            </a>
            .
          </p>
        </form>
      </main>
    </div>
  )
}

export default ContactPage
